import { gql } from "@apollo/client";

/**
 * All GraphQL operations for the Prospecting module live here
 * per the centralised-queries convention.
 * Types come from `src/generated/graphql.ts` after `yarn generate`.
 */

// ---------- Fragments ----------

export const CANDIDATE_CREDENTIALS = gql`
  fragment CandidateCredentialsCore on CandidateCredentials {
    resumeCid
    coverLetterCid
    workingRightsCid
    workingRightsStatus
    policeCheck {
      cid
      requestedAt
      receivedAt
      outcome
      disclosedConvictions
      requiresFurtherAssessment
      precluded
      assessedAt
      assessedById
      assessedByName
    }
    bannedRegisterCheckedAt
    bannedRegisterListed
    ahpraNumber
    ahpraCid
    ahpraExpiry
    medicationsEndorsement
    certificates {
      type
      cid
      label
    }
    cprCid
    cprExpiry
    firstAidCid
    firstAidExpiry
    fluImmunised
    covidImmunised
  }
`;

export const CANDIDATE_CORE = gql`
  fragment CandidateCore on Candidate {
    id
    tenantId
    firstName
    middleName
    dateOfBirth
    addressStreetNumber
    addressStreetName
    addressSuburb
    addressState
    addressPostcode
    addressCountry
    lastName
    email
    phone
    stage
    status
    jobTitle
    roleCategory
    companyId
    formId
    earliestStart
    departmentId
    locationId
    rateContext
    urgency
    source
    sourceNote
    resumeFileId
    coverLetterFileId
    certificateFileIds
    policeCheckFileId
    resumeUrl
    coverLetterUrl
    policeCheckUrl
    workingRightsUrl
    ahpraUrl
    cprUrl
    firstAidUrl
    certificateUrls
    credentials {
      ...CandidateCredentialsCore
    }
    credentialChecklist {
      complete
      required {
        key
        label
      }
      missing {
        key
        label
      }
    }
    preScreen {
      answers {
        key
        value
      }
      notes
      outcome
      completedAt
      completedById
      completedByName
    }
    previousApplications {
      candidateId
      stage
      at
    }
    referencesReceived
    referencesRequired
    phoneScreeningNotes
    phoneScreeningAt
    rejectionReason
    referenceContacts {
      name
      relation
      phone
      email
    }
    interviewNotes {
      date
      interviewerId
      feedback
      rating
    }
    chatRoomSlug
    preScreeningInviteId
    preScreeningSubmittedAt
    preScreeningAnswers
    convertedToEmployeeId
    convertedAt
    chaseConfigId
    tags
    createdAt
    updatedAt
  }
  ${CANDIDATE_CREDENTIALS}
`;

export const CANDIDATE_INVITE_CORE = gql`
  fragment CandidateInviteCore on CandidateInvite {
    id
    tenantId
    slug
    candidateId
    formId
    type
    status
    expiresAt
    emailSentAt
    smsSentAt
    submittedAt
    submissionId
    createdByName
    customMessage
    createdAt
  }
`;

export const CANDIDATE_FORM_CORE = gql`
  fragment CandidateFormCore on CandidateForm {
    id
    tenantId
    slug
    name
    description
    jobTitle
    isActive
    isDefault
    employerName
    employerLogoUrl
    employerBrandColor
    positionTitle
    roleCategory
    companyId
    earliestStartRequired
    minReferences
    preScreenQuestions {
      key
      label
      type
      order
    }
    fields {
      key
      label
      type
      required
      helpText
      options
      order
      fileAcceptTypes
      minReferences
      maxReferences
    }
  }
`;

export const CANDIDATE_MESSAGE_CORE = gql`
  fragment CandidateMessageCore on CandidateMessage {
    id
    tenantId
    candidateId
    channel
    context
    goal
    emailSubject
    emailBody
    smsContent
    smsCharCount
    status
    scheduledAt
    sentAt
    failureReason
    parentDraftId
    scheduledCalendarEventId
    meetingUrl
    formUrl
    createdByUserId
    createdAt
    updatedAt
  }
`;

export const PROSPECTING_NOTIFICATION_RULE_CORE = gql`
  fragment ProspectingNotificationRuleCore on ProspectingNotificationRule {
    id
    tenantId
    name
    trigger
    recipientType
    recipientIds
    channels
    messageTemplate
    isEnabled
    filterDepartmentId
    chatSessionGapMinutes
    chatRecipientCooldownMinutes
    createdAt
    updatedAt
  }
`;

// ---------- Queries (authenticated) ----------

export const GET_CANDIDATES = gql`
  query GetCandidates {
    candidates {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const GET_CANDIDATE = gql`
  query GetCandidate($id: ID!) {
    candidate(id: $id) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const GET_CANDIDATE_INVITES = gql`
  query GetCandidateInvites($candidateId: ID) {
    candidateInvites(candidateId: $candidateId) {
      ...CandidateInviteCore
    }
  }
  ${CANDIDATE_INVITE_CORE}
`;

export const GET_CANDIDATE_FORMS = gql`
  query GetCandidateForms {
    candidateForms {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

export const GET_CANDIDATE_FORM_SUBMISSIONS = gql`
  query GetCandidateFormSubmissions($candidateId: ID!) {
    candidateFormSubmissions(candidateId: $candidateId) {
      id
      candidateId
      inviteId
      formId
      submittedAt
      ipAddress
      userAgent
      answers {
        key
        value
        values
      }
    }
  }
`;

// ---------- Queries (public slug-based) ----------

export const GET_CANDIDATE_INVITE_BY_SLUG = gql`
  query GetCandidateInviteBySlug($slug: String!) {
    candidateInviteBySlug(slug: $slug) {
      inviteId
      candidateId
      firstName
      jobTitle
      formId
      companyName
      type
      status
      expiresAt
      isOpenForm
    }
  }
`;

export const GET_CANDIDATE_FORM_BY_ID = gql`
  query GetCandidateFormById($id: ID!) {
    candidateFormById(id: $id) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

// ---------- Mutations ----------

export const CREATE_CANDIDATE = gql`
  mutation CreateCandidate($input: CreateCandidateInput!) {
    createCandidate(input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const UPDATE_CANDIDATE = gql`
  mutation UpdateCandidate($id: ID!, $input: UpdateCandidateInput!) {
    updateCandidate(id: $id, input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const ADVANCE_CANDIDATE_STAGE = gql`
  mutation AdvanceCandidateStage($id: ID!, $stage: CandidateStage!, $reason: String) {
    advanceCandidateStage(id: $id, stage: $stage, reason: $reason) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const DELETE_CANDIDATE = gql`
  mutation DeleteCandidate($id: ID!) {
    deleteCandidate(id: $id)
  }
`;

export const CREATE_CANDIDATE_INVITE = gql`
  mutation CreateCandidateInvite($input: CreateCandidateInviteInput!) {
    createCandidateInvite(input: $input) {
      success
      publicUrl
      emailSent
      smsSent
      warning
      invite {
        ...CandidateInviteCore
      }
    }
  }
  ${CANDIDATE_INVITE_CORE}
`;

export const SUBMIT_CANDIDATE_FORM_VIA_SLUG = gql`
  mutation SubmitCandidateFormViaSlug($input: SubmitCandidateFormViaSlugInput!) {
    submitCandidateFormViaSlug(input: $input) {
      success
      message
      candidateId
      submissionId
    }
  }
`;

// ---------- Tom-manager integration (Phase 2: "Create AI chase" button) ----------
// Talks to the tom-manager module via its public GraphQL mutation. PLC doesn't
// import anything from tom-manager's JS modules — boundary stays clean.

export const CREATE_CLIENT_CHASE_CONFIG = gql`
  mutation CreateClientChaseConfigForCandidate($input: CreateClientChaseConfigInput!) {
    createClientChaseConfig(input: $input) {
      id
      targetTenantId
      targetTenantName
      targetCandidateId
      targetClientName
      targetClientEmail
      targetTenantUrl
      aiPrompt
      clientGoal
      channel
      schedule
      scheduleTime
      timezone
      isActive
      createdAt
    }
  }
`;

// ---------- Phase 1.5 extras ----------

export const REJECT_CANDIDATE = gql`
  mutation RejectCandidate($id: ID!, $reason: String) {
    rejectCandidate(id: $id, reason: $reason) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const MARK_PHONE_SCREENING_COMPLETE = gql`
  mutation MarkPhoneScreeningComplete($id: ID!, $notes: String, $advanceToShortlist: Boolean) {
    markPhoneScreeningComplete(id: $id, notes: $notes, advanceToShortlist: $advanceToShortlist) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const SET_CANDIDATE_POLICE_CHECK = gql`
  mutation SetCandidatePoliceCheck($id: ID!, $fileId: ID) {
    setCandidatePoliceCheck(id: $id, fileId: $fileId) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

// ---------- Phase 2.5 — form CRUD ----------

export const CREATE_CANDIDATE_FORM = gql`
  mutation CreateCandidateForm($input: CreateCandidateFormInput!) {
    createCandidateForm(input: $input) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

export const UPDATE_CANDIDATE_FORM = gql`
  mutation UpdateCandidateForm(
    $id: ID!
    $name: String
    $description: String
    $jobTitle: String
    $fields: [CandidateFormFieldInput!]
    $isDefault: Boolean
    $employerName: String
    $employerLogoUrl: String
    $employerBrandColor: String
  ) {
    updateCandidateForm(
      id: $id
      name: $name
      description: $description
      jobTitle: $jobTitle
      fields: $fields
      isDefault: $isDefault
      employerName: $employerName
      employerLogoUrl: $employerLogoUrl
      employerBrandColor: $employerBrandColor
    ) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

export const CLONE_CANDIDATE_FORM = gql`
  mutation CloneCandidateForm($sourceId: ID!, $newName: String!) {
    cloneCandidateForm(sourceId: $sourceId, newName: $newName) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

export const DELETE_CANDIDATE_FORM = gql`
  mutation DeleteCandidateForm($id: ID!) {
    deleteCandidateForm(id: $id)
  }
`;

// ---------- Phase 2 — AI compose + drafts thread ----------

export const GET_CANDIDATE_MESSAGES = gql`
  query GetCandidateMessages($candidateId: ID!) {
    candidateMessages(candidateId: $candidateId) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const DRAFT_CANDIDATE_MESSAGE = gql`
  mutation DraftCandidateMessage($input: DraftCandidateMessageInput!) {
    draftCandidateMessage(input: $input) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const REFINE_CANDIDATE_MESSAGE = gql`
  mutation RefineCandidateMessage($input: RefineCandidateMessageInput!) {
    refineCandidateMessage(input: $input) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const SEND_CANDIDATE_MESSAGE_NOW = gql`
  mutation SendCandidateMessageNow($draftId: ID!) {
    sendCandidateMessageNow(draftId: $draftId) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const SCHEDULE_CANDIDATE_MESSAGE = gql`
  mutation ScheduleCandidateMessage($draftId: ID!, $scheduledAt: String!) {
    scheduleCandidateMessage(draftId: $draftId, scheduledAt: $scheduledAt) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const CANCEL_SCHEDULED_CANDIDATE_MESSAGE = gql`
  mutation CancelScheduledCandidateMessage($draftId: ID!) {
    cancelScheduledCandidateMessage(draftId: $draftId) {
      ...CandidateMessageCore
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

export const DELETE_CANDIDATE_DRAFT = gql`
  mutation DeleteCandidateDraft($draftId: ID!) {
    deleteCandidateDraft(draftId: $draftId)
  }
`;

// ---------- Phase 2 — schedule interview ----------

export const SCHEDULE_CANDIDATE_INTERVIEW = gql`
  mutation ScheduleCandidateInterview($input: ScheduleInterviewInput!) {
    scheduleCandidateInterview(input: $input) {
      success
      calendarEventId
      meetingUrl
      meetRoomCreated
      draftMessage {
        ...CandidateMessageCore
      }
    }
  }
  ${CANDIDATE_MESSAGE_CORE}
`;

// ---------- Phase 2.6 — candidate chat ----------

export const ENSURE_CANDIDATE_CHAT_ROOM = gql`
  mutation EnsureCandidateChatRoom($candidateId: ID!) {
    ensureCandidateChatRoom(candidateId: $candidateId) {
      roomId
      slug
      createdNew
    }
  }
`;

export const START_CANDIDATE_CHAT = gql`
  mutation StartCandidateChat(
    $candidateId: ID!
    $sendEmail: Boolean
    $sendSms: Boolean
    $customMessage: String
  ) {
    startCandidateChat(
      candidateId: $candidateId
      sendEmail: $sendEmail
      sendSms: $sendSms
      customMessage: $customMessage
    ) {
      success
      roomId
      slug
      publicUrl
      emailSent
      smsSent
      warning
    }
  }
`;

// ---------- Phase 2.8 — notification rules ----------

export const GET_PROSPECTING_NOTIFICATION_RULES = gql`
  query GetProspectingNotificationRules {
    prospectingNotificationRules {
      ...ProspectingNotificationRuleCore
    }
  }
  ${PROSPECTING_NOTIFICATION_RULE_CORE}
`;

export const CREATE_PROSPECTING_NOTIFICATION_RULE = gql`
  mutation CreateProspectingNotificationRule($input: CreateProspectingNotificationRuleInput!) {
    createProspectingNotificationRule(input: $input) {
      ...ProspectingNotificationRuleCore
    }
  }
  ${PROSPECTING_NOTIFICATION_RULE_CORE}
`;

export const UPDATE_PROSPECTING_NOTIFICATION_RULE = gql`
  mutation UpdateProspectingNotificationRule($id: ID!, $input: UpdateProspectingNotificationRuleInput!) {
    updateProspectingNotificationRule(id: $id, input: $input) {
      ...ProspectingNotificationRuleCore
    }
  }
  ${PROSPECTING_NOTIFICATION_RULE_CORE}
`;

export const DELETE_PROSPECTING_NOTIFICATION_RULE = gql`
  mutation DeleteProspectingNotificationRule($id: ID!) {
    deleteProspectingNotificationRule(id: $id)
  }
`;

export const TOGGLE_PROSPECTING_ALL_EVENTS = gql`
  mutation ToggleProspectingAllEventsSubscription($enabled: Boolean!) {
    toggleProspectingAllEventsSubscription(enabled: $enabled)
  }
`;

export const GET_MY_PROSPECTING_ALL_EVENTS_SUBSCRIPTION = gql`
  query MyProspectingAllEventsSubscription {
    myProspectingAllEventsSubscription
  }
`;

// Lookup query for the employee-search picker inside NotificationRules.
// Mirrors the shape used by employees + shifts notification pages.
export const GET_PROSPECTING_EMPLOYEE_OPTIONS = gql`
  query GetProspectingEmployeeOptions($input: EmployeePaginationInput) {
    employeesPaginated(input: $input) {
      employees {
        id
        preferredName
        client {
          id
          fName
          lName
          email
        }
      }
    }
  }
`;

// Phase 16c — department filter on a rule (dept picker in modal body).
export const GET_PROSPECTING_DEPARTMENTS = gql`
  query GetProspectingDepartments {
    departments {
      id
      code
      name
      displayOrder
    }
  }
`;

// ---------- Home dashboard (src/pages/home) — lean overview reads ----------
// Deliberately scalar-only: the Home overview counts and lists candidates and
// screening forms without pulling the field/answer sub-documents the full
// fragments carry. Types: Pick<Candidate | CandidateForm, …> from generated/graphql.ts.
export const GET_CANDIDATE_OVERVIEW = gql`
  query GetCandidateOverview {
    candidates {
      id
      firstName
      lastName
      jobTitle
      stage
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_CANDIDATE_FORM_STATES = gql`
  query GetCandidateFormStates {
    candidateForms {
      id
      name
      jobTitle
      employerName
      slug
      isActive
    }
  }
`;

// ---------- Pipeline v0.2 — pre-screen / interview / references / credentials / activity / hand-over ----------

export const CANDIDATE_REFERENCE_CORE = gql`
  fragment CandidateReferenceCore on CandidateReference {
    id
    candidateId
    name
    relation
    phone
    email
    method
    status
    token
    answers {
      key
      value
    }
    rating
    notes
    requestedAt
    emailSentAt
    receivedAt
    checkedById
    checkedByName
    createdAt
    updatedAt
  }
`;

export const CANDIDATE_ACTIVITY_CORE = gql`
  fragment CandidateActivityCore on CandidateActivity {
    id
    candidateId
    type
    byId
    byName
    summary
    details
    at
    createdAt
  }
`;

export const GET_CANDIDATE_REFERENCES = gql`
  query GetCandidateReferences($candidateId: ID!) {
    candidateReferences(candidateId: $candidateId) {
      ...CandidateReferenceCore
    }
  }
  ${CANDIDATE_REFERENCE_CORE}
`;

export const GET_CANDIDATE_ACTIVITY = gql`
  query GetCandidateActivity($candidateId: ID!) {
    candidateActivity(candidateId: $candidateId) {
      ...CandidateActivityCore
    }
  }
  ${CANDIDATE_ACTIVITY_CORE}
`;

export const COMPLETE_PRE_SCREEN = gql`
  mutation CompletePreScreen($id: ID!, $input: CompletePreScreenInput!) {
    completePreScreen(id: $id, input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const COMPLETE_INTERVIEW = gql`
  mutation CompleteInterview($id: ID!, $input: CompleteInterviewInput!) {
    completeInterview(id: $id, input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const REQUEST_REFERENCE = gql`
  mutation RequestReference($input: RequestReferenceInput!) {
    requestReference(input: $input) {
      publicUrl
      emailSent
      warning
      reference {
        ...CandidateReferenceCore
      }
    }
  }
  ${CANDIDATE_REFERENCE_CORE}
`;

export const RECORD_PHONE_REFERENCE = gql`
  mutation RecordPhoneReference($id: ID!, $input: ReferenceAnswersInput!) {
    recordPhoneReference(id: $id, input: $input) {
      ...CandidateReferenceCore
    }
  }
  ${CANDIDATE_REFERENCE_CORE}
`;

export const SET_CANDIDATE_CREDENTIAL = gql`
  mutation SetCandidateCredential($id: ID!, $input: CandidateCredentialInput!) {
    setCandidateCredential(id: $id, input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const ASSESS_POLICE_CHECK = gql`
  mutation AssessPoliceCheck($id: ID!, $input: PoliceCheckAssessmentInput!) {
    assessPoliceCheck(id: $id, input: $input) {
      ...CandidateCore
    }
  }
  ${CANDIDATE_CORE}
`;

export const ADD_CANDIDATE_NOTE = gql`
  mutation AddCandidateNote($candidateId: ID!, $note: String!) {
    addCandidateNote(candidateId: $candidateId, note: $note) {
      ...CandidateActivityCore
    }
  }
  ${CANDIDATE_ACTIVITY_CORE}
`;

export const PUT_FORWARD_CANDIDATE = gql`
  mutation PutForwardCandidate($id: ID!, $companyId: ID) {
    putForwardCandidate(id: $id, companyId: $companyId) {
      created
      recommendationId
      targetTenantId
      targetTenantName
      emailTo
      inAppRecipients
      link
      candidate {
        ...CandidateCore
      }
    }
  }
  ${CANDIDATE_CORE}
`;

export const UPDATE_CANDIDATE_FORM_POSITION = gql`
  mutation UpdateCandidateFormPosition($id: ID!, $input: UpdateCandidateFormPositionInput!) {
    updateCandidateFormPosition(id: $id, input: $input) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

// Lean employer picker (companies module) — the module owns the read it needs;
// no sibling import of companies/queries.ts. Types: Pick<Company, "id" | "name" | ...>.
export const GET_PROSPECTING_COMPANY_OPTIONS = gql`
  query GetProspectingCompanyOptions {
    companies {
      id
      name
      tradingName
      isActive
      linkedTenantId
      recruitmentContactEmail
    }
  }
`;

// ---------- Public (tenantMiddleware only — the token / slug is the gate) ----------

export const GET_CANDIDATE_FORM_BY_SLUG = gql`
  query GetCandidateFormBySlug($formSlug: String!) {
    candidateFormBySlug(formSlug: $formSlug) {
      ...CandidateFormCore
    }
  }
  ${CANDIDATE_FORM_CORE}
`;

export const GET_PUBLIC_REFERENCE_BY_TOKEN = gql`
  query GetPublicReferenceByToken($token: String!) {
    publicReferenceByToken(token: $token) {
      refereeName
      candidateName
      positionTitle
      companyName
      status
      alreadySubmitted
      expired
      expiresAt
    }
  }
`;

export const SUBMIT_REFERENCE_BY_TOKEN = gql`
  mutation SubmitReferenceByToken($token: String!, $input: ReferenceAnswersInput!) {
    submitReferenceByToken(token: $token, input: $input) {
      success
      message
    }
  }
`;

export const EXTRACT_CANDIDATE_FROM_DOCUMENT = gql`
  mutation ExtractCandidateFromDocument($input: CandidateExtractionInput!) {
    extractCandidateFromDocument(input: $input) {
      firstName middleName lastName email phone jobTitle rateContext dateOfBirth
      addressStreetNumber addressStreetName addressSuburb addressState addressPostcode addressCountry warnings
    }
  }
`;

export const GET_INTERVIEW_CALENDARS = gql`
  query GetInterviewCalendars {
    calendars(type: BUSINESS) { id name allowPublicBooking bookingPageSlug }
  }
`;
